import { IGameplayEvent } from '../../../Core/State/IJoinedRoomState';

export interface ActionBadgeState {
    action: string;
    amount: number;
    timestamp: number;
}

export interface WinnerIndicatorState {
    amount: number;
    timestamp: number;
}

export interface GameRoomPresentationState {
    actionBadges: { [userId: string]: ActionBadgeState | undefined };
    winnerIndicators: { [userId: string]: WinnerIndicatorState | undefined };
    visibleCommunityCards: string[];
    animatedCommunityCardDelaysMs: { [cardIndex: number]: number };
    showdownCardsRevealKey: string | null;
    playerCardsVisible: boolean;
    completedHandPresentationFadingOut: boolean;
}

export interface PlayerActionBadgePresentation {
    userId: string;
    badge: ActionBadgeState;
    shouldClearCurrentUserAutoAction: boolean;
}

export interface WinnerIndicatorsPresentation {
    winnerIndicators: GameRoomPresentationState['winnerIndicators'];
    balanceAnimations: Array<{
        userId: string;
        amount: number;
    }>;
}

const TERMINAL_ACTION_BADGES = new Set(['fold', 'all-in']);

export interface PokerPlayerPresentationApplierState<SelectedAutoAction> extends GameRoomPresentationState {
    selectedAutoAction: SelectedAutoAction | null;
}

export type PokerPlayerPresentationApplierPatch<SelectedAutoAction> = Pick<
    PokerPlayerPresentationApplierState<SelectedAutoAction>,
    'actionBadges' | 'winnerIndicators' | 'selectedAutoAction'
>;

export interface PokerPlayerPresentationApplierOptions<SelectedAutoAction> {
    updateState: (
        updater: (
            prevState: PokerPlayerPresentationApplierState<SelectedAutoAction>
        ) => PokerPlayerPresentationApplierPatch<SelectedAutoAction> | null
    ) => void;
    triggerBalanceChangeAnimation: (userId: string) => void;
    queueAnimation: (amount: number) => void;
}

export const createPlayerActionBadgePresentation = (
    event: IGameplayEvent,
    currentUserId: string | undefined
): PlayerActionBadgePresentation | null => {
    if (!event.actorUserId || !event.action) return null;

    const amount = typeof event.amount === 'number' ? event.amount : 0;
    return {
        userId: event.actorUserId,
        badge: {
            action: event.action,
            amount,
            timestamp: Date.now()
        },
        shouldClearCurrentUserAutoAction: event.actorUserId === currentUserId
    };
};

export const createWinnerIndicatorsPresentation = (
    event: IGameplayEvent
): WinnerIndicatorsPresentation | null => {
    const winnerDetails = Array.isArray(event.winners) ? event.winners : [];
    const winnerIds = Array.isArray(event.winnerIds) ? event.winnerIds : [];
    const uniqueWinnerIds = Array.from(new Set([
        ...winnerDetails.map((winner) => winner.userId).filter(Boolean),
        ...winnerIds.filter(Boolean)
    ]));

    if (uniqueWinnerIds.length === 0) return null;

    const winnerIndicators: GameRoomPresentationState['winnerIndicators'] = {};
    const balanceAnimations: WinnerIndicatorsPresentation['balanceAnimations'] = [];

    uniqueWinnerIds.forEach((winnerId) => {
        const winner = winnerDetails.find((candidate) => candidate.userId === winnerId);
        const amount = typeof winner?.winAmount === 'number' ? winner.winAmount : 0;
        winnerIndicators[winnerId] = {
            amount,
            timestamp: Date.now()
        };
        if (amount > 0) {
            balanceAnimations.push({ userId: winnerId, amount });
        }
    });

    return {
        winnerIndicators,
        balanceAnimations
    };
};

export class PokerPlayerPresentationApplier<SelectedAutoAction> {
    constructor(private readonly options: PokerPlayerPresentationApplierOptions<SelectedAutoAction>) {}

    public applyActionBadgePresentation = (presentation: PlayerActionBadgePresentation) => {
        this.options.updateState((prevState) => {
            return {
                actionBadges: {
                    ...prevState.actionBadges,
                    [presentation.userId]: presentation.badge
                },
                winnerIndicators: prevState.winnerIndicators,
                selectedAutoAction: presentation.shouldClearCurrentUserAutoAction
                    ? null
                    : prevState.selectedAutoAction
            };
        });

        if (presentation.badge.amount > 0) {
            this.options.triggerBalanceChangeAnimation(presentation.userId);
            this.options.queueAnimation(presentation.badge.amount);
        }
    };

    public clearActionBadge = (userId: string | undefined) => {
        if (!userId) return;

        this.options.updateState((prevState) => {
            if (!prevState.actionBadges[userId]) return null;

            const nextActionBadges = { ...prevState.actionBadges };
            delete nextActionBadges[userId];
            return {
                actionBadges: nextActionBadges,
                winnerIndicators: prevState.winnerIndicators,
                selectedAutoAction: prevState.selectedAutoAction
            };
        });
    };

    public clearActionBadges = () => {
        this.options.updateState((prevState) => (
            Object.keys(prevState.actionBadges).length === 0
                ? null
                : {
                    actionBadges: {},
                    winnerIndicators: prevState.winnerIndicators,
                    selectedAutoAction: prevState.selectedAutoAction
                }
        ));
    };

    public clearNonTerminalActionBadges = () => {
        this.options.updateState((prevState) => {
            const retainedActionBadges = Object.fromEntries(
                Object.entries(prevState.actionBadges).filter(([, badge]) => (
                    badge && TERMINAL_ACTION_BADGES.has(badge.action)
                ))
            );

            if (Object.keys(retainedActionBadges).length === Object.keys(prevState.actionBadges).length) {
                return null;
            }

            return {
                actionBadges: retainedActionBadges,
                winnerIndicators: prevState.winnerIndicators,
                selectedAutoAction: prevState.selectedAutoAction
            };
        });
    };

    public applyWinnerIndicatorsPresentation = (presentation: WinnerIndicatorsPresentation) => {
        this.options.updateState((prevState) => ({
            actionBadges: prevState.actionBadges,
            winnerIndicators: presentation.winnerIndicators,
            selectedAutoAction: prevState.selectedAutoAction
        }));
        presentation.balanceAnimations.forEach(({ userId, amount }) => {
            this.options.triggerBalanceChangeAnimation(userId);
            this.options.queueAnimation(amount);
        });
    };

    public clearWinnerIndicators = () => {
        this.options.updateState((prevState) => (
            Object.keys(prevState.winnerIndicators).length === 0
                ? null
                : {
                    actionBadges: prevState.actionBadges,
                    winnerIndicators: {},
                    selectedAutoAction: prevState.selectedAutoAction
                }
        ));
    };
}
